/* eslint-disable @typescript-eslint/no-misused-promises */
'use client';

import { useState } from 'react';
import Image from 'next/image';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../Collapsible';
import { abbreviateNumber, formatDate, formatNumber, formatWalletAddress } from 'mode-shared/utils';
import { CopyButton } from '../CopyButton';
import { Tooltip } from '../Tooltip';
import { CombinedCampaign } from 'mode-shared/hooks/dashboard/types';
import Link from 'next/link';
import { ExternalLinkIcon } from '../icons';
import { DetailRow } from './DetailRow';

export function DetailsCollapsiblePanel({ campaign }: { campaign: CombinedCampaign }) {
  const [isOpen, setIsOpen] = useState(false);

  const formattedToken0Weight = campaign.token_0_weight / 100;
  const formattedToken1Weight = campaign.token_1_weight / 100;
  const formattedFeesWeight = campaign.fees_weight / 100;

  const campaignType = campaign.token_0_address && campaign.token_1_address ? 2 : 1;
  const isType1Campaign = campaignType === 1;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full rounded-lg bg-transparent text-grey-300">
      <CollapsibleTrigger className="flex items-center bg-neutral-900 justify-between w-full p-3 hover:bg-neutral-900/90">
        <div className="flex items-center gap-2">
          <span className="font-primary font-semibold text-sm uppercase text-grey-600">Details</span>{' '}
          <Tooltip trigger={<Image src="/assets/icons/info.svg" width={16} height={16} alt="info icon" />}>
            Click to reveal campaign details. This information is automatically generated from the Merkl and Fuul
            platforms.
          </Tooltip>
        </div>
        {isOpen ? (
          <Image src="/assets/icons/chevronUp.svg" width={16} height={16} alt="chevron up icon" />
        ) : (
          <Image src="/assets/icons/chevronDown.svg" width={16} height={16} alt="chevron down icon" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="p-4">
        <div className="flex flex-col divide-y divide-neutral-600/25">
          <DetailRow label="Reward Period">
            <p className="font-sans font-medium text-xs text-grey-500 uppercase">
              {campaign.start_date && formatDate(campaign.start_date, 'dd MMM yyyy')} -{' '}
              {campaign.end_date && formatDate(campaign.end_date, 'dd MMM yyyy')}
            </p>
          </DetailRow>
          <DetailRow label="Address">
            <div className="flex items-center gap-2">
              {formatWalletAddress(campaign.pool_address)}
              <CopyButton size="sm" copyValue={campaign.pool_address} />
            </div>
          </DetailRow>

          <DetailRow label="Token Address">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="text-grey-500 font-medium">{formatWalletAddress(campaign.token_0_address)}</span>
                  <span className="uppercase">{campaign.token_0_name}</span>
                </div>
                <CopyButton size="sm" copyValue={campaign.token_0_address} />
              </div>
              {!isType1Campaign && (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="text-grey-500 font-medium">{formatWalletAddress(campaign.token_1_address)}</span>
                    <span className="uppercase">{campaign.token_1_name}</span>
                  </div>
                  <CopyButton size="sm" copyValue={campaign.token_1_address} />
                </div>
              )}
            </div>
          </DetailRow>

          <DetailRow label="Rewards Available">
            <div className="flex items-center gap-2">
              {campaign.reward_tokens?.map((reward) => (
                <div key={reward.reward_token_address} className="flex gap-1">
                  <span className="text-grey-500 font-medium">{formatNumber(reward.reward_token_amount)}</span>
                  <span>{reward.reward_token_name}</span>
                </div>
              ))}
            </div>
          </DetailRow>

          {!isType1Campaign && (
            <DetailRow label="Weight">
              {campaign.hasMoreThanOneCampaign ? (
                <p className="flex items-center gap-1 font-sans font-medium text-xs text-grey-600">
                  Varies - check campaigns
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={campaign.campaign_url || 'https://merkl.angle.money/?chain=34443'}
                    className="flex items-center justify-center border-grey-700 bg-neutral-950 rounded"
                  >
                    <ExternalLinkIcon className="text-neutral-100" size={16} />
                  </Link>
                </p>
              ) : (
                <div className="w-full flex gap-1">
                  <span className="text-grey-500 font-medium">{`${formattedToken0Weight}% ${formattedToken1Weight}% ${formattedFeesWeight}%`}</span>
                  <span className="text-grey-500 font-medium">{`(${campaign.token_0_name}-${campaign.token_1_name}-Fees)`}</span>
                </div>
              )}
            </DetailRow>
          )}

          <DetailRow label="In Range">
            {campaign.hasMoreThanOneCampaign ? (
              <p className="flex items-center gap-1 font-sans font-medium text-xs text-grey-600">
                Varies - check campaigns
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={campaign.campaign_url || 'https://merkl.angle.money/?chain=34443'}
                  className="flex items-center justify-center border-grey-700 bg-neutral-950 rounded"
                >
                  <ExternalLinkIcon className="text-neutral-100" size={16} />
                </Link>
              </p>
            ) : (
              <span className="text-grey-500 font-medium">{campaign.is_in_range ? 'Yes' : 'No'}</span>
            )}
          </DetailRow>

          <DetailRow label="Apr">
            <span className="text-grey-500 font-medium">{formatNumber(campaign.apr || 0)}%</span>
          </DetailRow>

          <DetailRow label="Tvl">
            <span className="text-grey-500 font-medium">{abbreviateNumber(campaign.pool_tvl || 0)}</span>
          </DetailRow>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
